import { env } from '../env'

export class ApiError extends Error {
  constructor(message: string, public readonly status: number, public readonly body?: unknown) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Fetch wrapper that talks to the reset-service admin API.
 *
 * - `credentials: 'include'` — session cookie is HttpOnly + SameSite=Lax.
 * - Throws `ApiError` on non-2xx so React Query captures it as an error.
 * - 401 is returned as-is; the caller (RequireAuth) redirects to /login.
 */
export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
  base?: string,
): Promise<T> {
  const url = (base ?? env.apiBaseUrl) + path
  // Fastify rejects a POST/PATCH/PUT with `content-type: application/json`
  // and no body with 400 "Body cannot be empty when content-type is set
  // to 'application/json'". Our POST-only endpoints (reprocess, reset,
  // logout) genuinely have no body, so only set the JSON header when
  // there IS a body — and only merge caller-supplied headers on top.
  const method = (init.method ?? 'GET').toUpperCase()
  const wantsJson = init.body !== undefined && init.body !== null
  const headers: Record<string, string> = {
    ...(wantsJson ? { 'Content-Type': 'application/json' } : {}),
    ...(init.headers as Record<string, string> | undefined ?? {}),
  }
  const res = await fetch(url, {
    ...init,
    method,
    credentials: 'include',
    headers,
  })
  if (res.status === 204) {
    return undefined as T
  }
  let body: unknown
  const text = await res.text()
  if (text) {
    try { body = JSON.parse(text) } catch { body = text }
  }
  if (!res.ok) {
    const message =
      body && typeof body === 'object' && 'message' in body
        ? String((body as { message: unknown }).message)
        : res.statusText
    throw new ApiError(message, res.status, body)
  }
  return body as T
}

interface DashboardMetrics {
  invoices: { today: number; last7Days: number; last30Days: number }
  successRate: number
  failedQueueDepth: number
  pendingNavSubmissions: number
}

export const api = {
  admin: {
    /**
     * Dashboard metrics scoped to the caller's integrator. The backend
     * endpoint ships in Wave 2; until then this returns 404 and the
     * UI falls through to zero-state cards.
     */
    metrics: () => apiFetch<DashboardMetrics>('/v1/admin/metrics'),
  },
  auth: {
    tenantInfo: () =>
      apiFetch<{ slug: string; displayName: string; provider: 'google' | 'microsoft' | 'generic' | null }>(
        '/v1/auth/tenant-info',
        {},
        env.authBaseUrl,
      ),
    me: () =>
      apiFetch<{
        email: string
        role: 'admin' | 'viewer'
        integratorId: string
        integratorSlug: string
        integratorName: string
        isSystemAdmin: boolean
        expiresAt: string
      }>('/v1/auth/me', {}, env.authBaseUrl),
    logout: () => apiFetch<void>('/v1/auth/logout', { method: 'POST' }, env.authBaseUrl),
  },
  integrators: {
    list: (limit = 50, offset = 0) =>
      apiFetch<IntegratorRow[]>(
        `/v1/admin/integrators?limit=${limit}&offset=${offset}`,
      ),
    setSigning: (id: string, enabled: boolean) =>
      apiFetch<{ id: string; signingEnabled: boolean }>(
        `/v1/admin/integrators/${id}/signing`,
        { method: 'PATCH', body: JSON.stringify({ enabled }) },
      ),
    rotateKey: (id: string) =>
      apiFetch<{ keyId: string; algorithm: string; status: string }>(
        `/v1/admin/integrators/${id}/signing-keys/rotate`,
        { method: 'POST' },
      ),
    updateProfile: (id: string, body: { name?: string; callbackUrl?: string }) =>
      apiFetch<{ id: string; name: string; callbackUrl: string | null }>(
        `/v1/admin/integrators/${id}/profile`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
  },
  merchants: {
    listForIntegrator: (integratorId: string, limit = 50, offset = 0) =>
      apiFetch<{ total: number; items: MerchantRow[] }>(
        `/v1/admin/integrators/${integratorId}/merchants?limit=${limit}&offset=${offset}`,
      ),
    create: (integratorId: string, body: { displayName: string; externalId?: string }) =>
      apiFetch<{ id: string; displayName: string; status: string; externalId: string }>(
        `/v1/admin/integrators/${integratorId}/merchants`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    detail: (merchantId: string) =>
      apiFetch<MerchantDetail>(`/v1/admin/merchants/${merchantId}`),
    addTaxableEntity: (
      merchantId: string,
      body: { integratorId: string; countryCode: string; taxNumber: string; entityType?: string },
    ) =>
      apiFetch<{ id: string; taxNumber: string; countryCode: string }>(
        `/v1/admin/merchants/${merchantId}/taxable-entities`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    registerNavUser: (
      merchantId: string,
      body: {
        integratorId: string
        taxableEntityId: string
        environment: 'test' | 'production'
        login: string
        taxNumber: string
        password: string
        signKey: string
        exchangeKey: string
      },
    ) =>
      apiFetch<{ id: string; secretRef: string }>(
        `/v1/admin/merchants/${merchantId}/nav-technical-users`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    revokeNavUser: (merchantId: string, id: string) =>
      apiFetch<{ id: string; status: 'revoked' }>(
        `/v1/admin/merchants/${merchantId}/nav-technical-users/${id}/revoke`,
        { method: 'POST' },
      ),
  },
  invoices: {
    list: (filters: InvoiceListFilters) => {
      const q = new URLSearchParams()
      if (filters.integratorId) q.set('integratorId', filters.integratorId)
      if (filters.merchantId) q.set('merchantId', filters.merchantId)
      if (filters.status) q.set('status', filters.status)
      if (filters.taxReportingStatus) q.set('taxReportingStatus', filters.taxReportingStatus)
      if (filters.dateFrom) q.set('dateFrom', filters.dateFrom)
      if (filters.dateTo) q.set('dateTo', filters.dateTo)
      q.set('limit', String(filters.limit ?? 50))
      q.set('offset', String(filters.offset ?? 0))
      return apiFetch<{ total: number; items: InvoiceRow[] }>(`/v1/admin/invoices?${q.toString()}`)
    },
    detail: (id: string) => apiFetch<InvoiceDetail>(`/v1/admin/invoices/${id}`),
    reprocess: (id: string) =>
      apiFetch<{ invoiceId: string; pdfJobsRequeued: number; navSubmissionJobsRequeued: number }>(
        `/v1/admin/invoices/${id}/reprocess`,
        { method: 'POST' },
      ),
    reset: (id: string) =>
      apiFetch<{
        invoiceId: string
        navJobsReset: number
        pdfJobsReset: number
        taxReportingStatusReset: boolean
      }>(
        `/v1/admin/invoices/${id}/reset`,
        { method: 'POST' },
      ),
  },
  callbacks: {
    list: (filters: CallbackListFilters) => {
      const q = new URLSearchParams()
      if (filters.integratorId) q.set('integratorId', filters.integratorId)
      if (filters.status)       q.set('status', filters.status)
      if (filters.eventName)    q.set('eventName', filters.eventName)
      if (filters.dateFrom)     q.set('dateFrom', filters.dateFrom)
      if (filters.dateTo)       q.set('dateTo', filters.dateTo)
      q.set('limit',  String(filters.limit  ?? 50))
      q.set('offset', String(filters.offset ?? 0))
      return apiFetch<{ total: number; items: CallbackRow[] }>(
        `/v1/admin/callbacks?${q.toString()}`,
      )
    },
    detail: (id: string) => apiFetch<CallbackDetail>(`/v1/admin/callbacks/${id}`),
    resend: (id: string) =>
      apiFetch<{
        id: string
        status: string
        attemptCount: number
        resendCount: number
        nextRetryAt: string | null
      }>(`/v1/admin/callbacks/${id}/resend`, { method: 'POST' }),
  },
}

export interface CallbackListFilters {
  integratorId?: string | undefined
  status?: string | undefined
  eventName?: string | undefined
  dateFrom?: string | undefined
  dateTo?: string | undefined
  limit?: number | undefined
  offset?: number | undefined
}

export interface CallbackRow {
  id: string
  integratorId: string
  integratorName: string | null
  merchantId: string
  merchantDisplayName: string | null
  jobId: string
  invoiceId: string | null
  invoiceNumber: string | null
  eventName: string | null
  status: string
  attemptCount: number
  sentAt: string | null
  nextRetryAt: string | null
  lastErrorPreview: string | null
  createdAt: string
  updatedAt: string
}

export interface CallbackDetail extends CallbackRow {
  callbackUrl: string | null
  lastError: string | null
  payload: unknown
  resendCount: number
  lastResentAt: string | null
  attempts: CallbackAttemptRow[]
}

export interface CallbackAttemptRow {
  id: string
  attemptNumber: number
  startedAt: string
  completedAt: string
  durationMs: number
  httpStatus: number | null
  outcome: 'sent' | 'failed'
  responseBody: string | null
  errorMessage: string | null
}

export interface IntegratorRow {
  id: string
  name: string
  slug?: string
  status: string
  signingEnabled?: boolean
  callbackUrl?: string
  createdAt?: string
}

export interface MerchantRow {
  id: string
  displayName: string
  status: string
  externalId: string
  validFrom: string
  connectedAt: string
}

export interface MerchantConnection {
  id: string
  integratorId: string
  externalId: string
  status: string
  validFrom: string
  validTo: string | null
}

export interface TaxableEntity {
  id: string
  integratorId: string
  countryCode: string
  taxNumber: string
  entityType: string
  validFrom: string
  validTo: string | null
  createdAt: string
}

export interface NavTechUser {
  id: string
  integratorId: string
  taxableEntityId: string
  environment: string
  login: string
  taxNumber: string
  status: string
  secretRef: string
  validFrom: string
  validTo: string | null
  createdAt: string
  lastTokenExchangeAt: string | null
}

export interface MerchantDetail {
  id: string
  displayName: string
  status: string
  createdAt: string
  connections: MerchantConnection[]
  taxableEntities: TaxableEntity[]
  navTechnicalUsers: NavTechUser[]
}

export interface InvoiceRow {
  id: string
  merchantId: string
  merchantDisplayName: string | null
  integratorId: string
  invoiceNumber: string | null
  status: string
  taxReportingStatus: string
  taxSubmissionId: string | null
  attemptCount: number
  createdAt: string
  updatedAt: string
  completedAt: string | null
  stornoOfInvoiceId: string | null
}

export interface InvoiceDetail extends InvoiceRow {
  countryCode: string
  invoiceBookId: string | null
  idempotencyKey: string | null
  /** RES-118: raw inbound DTO / payload — full invoice_data JSONB. */
  invoiceData: unknown
  taxSubmittedAt: string | null
  taxConfirmedAt: string | null
  taxReportData: unknown
  pdfS3Key: string | null
  pdfSha256: string | null
  navRequestXmlKey: string | null
  navInternalRequestXmlKey: string | null
  navResponseXmlKey: string | null
  navPollResponseXmlKey: string | null
  ublS3Key: string | null
  ublSha256: string | null
  ublValidationStatus: 'PASSED' | 'FAILED' | null
  ublValidationSvrlS3Key: string | null
  ublValidationSvrlSha256: string | null
  ublValidationPeppolSvrlS3Key: string | null
  ublValidationPeppolSvrlSha256: string | null
  ublValidationAt: string | null
  legacyStoredPdfUrl: string | null
  /** PAdES-B-LTA signature metadata. null until pdf-worker has signed the PDF. */
  signature: {
    level: string
    signerCertSha256: string
    signedAt: string
    tsaUrl: string
    refreshDueAt: string
  } | null
  events: InvoiceEvent[]
  latestReceipt: InvoiceReceipt | null
  downloadUrls: {
    pdf:                    string | null
    navRequestXml:          string | null
    navInternalRequestXml:  string | null
    navResponseXml:         string | null
    navPollResponseXml:     string | null
    ublXml:                     string | null
    ublValidationSvrlXml:       string | null
    ublValidationPeppolSvrlXml: string | null
  }
}

export interface InvoiceEvent {
  id: string
  fromState: string | null
  toState: string | null
  eventName: string
  triggeredBy: string
  meta: unknown
  at: string
}

export interface InvoiceReceipt {
  id: string
  payloadHash: string
  chainHash: string
  sigValid: boolean
  receivedAt: string
  requestId: string
}

export interface InvoiceListFilters {
  integratorId?: string | undefined
  merchantId?: string | undefined
  status?: string | undefined
  taxReportingStatus?: string | undefined
  dateFrom?: string | undefined
  dateTo?: string | undefined
  limit?: number | undefined
  offset?: number | undefined
}
